/**
 * Toolbar Component
 * 
 * Editor toolbar with undo/redo, split, delete, zoom controls.
 * Inspired by professional NLE toolbars.
 */

import React from 'react';
import { ToolbarProps } from '../types';

interface ExtendedToolbarProps extends ToolbarProps {
  clipCount?: number;
  modeToggle?: {
    mode: 'segments' | 'overlays';
    onModeChange: (mode: 'segments' | 'overlays') => void;
  };
}

const Toolbar: React.FC<ExtendedToolbarProps> = ({
  canUndo,
  canRedo,
  zoomLevel,
  onUndo,
  onRedo,
  onZoomChange,
  clipCount = 0,
  rightContent,
  modeToggle,
}) => {
  return (
    <div className="flex items-center justify-between px-4 py-2 bg-white border-b border-gray-200">
      {/* Left: History Controls */}
      <div className="flex items-center space-x-1">
        <button
          onClick={onUndo}
          disabled={!canUndo}
          className={`p-2 rounded transition-colors ${
            canUndo
              ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Undo (Ctrl+Z)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
        <button
          onClick={onRedo}
          disabled={!canRedo}
          className={`p-2 rounded transition-colors ${
            canRedo
              ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Redo (Ctrl+Y)"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
          </svg>
        </button>
      </div>

      {/* Center: Mode Toggle (if provided) */}
      {modeToggle && (
        <div className="flex items-center space-x-1 border-r border-gray-200 pr-3 mr-3">
          <button
            onClick={() => modeToggle.onModeChange('segments')}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              modeToggle.mode === 'segments'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Edit Segments
          </button>
          <button
            onClick={() => modeToggle.onModeChange('overlays')}
            className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
              modeToggle.mode === 'overlays'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            Add Overlays
          </button>
        </div>
      )}

      {/* Center: Zoom Controls */}
      <div className="flex items-center space-x-2">
        <button
          onClick={() => onZoomChange(zoomLevel - 0.25)}
          disabled={zoomLevel <= 0.5}
          className={`p-1.5 rounded transition-colors ${
            zoomLevel > 0.5
              ? 'text-gray-600 hover:bg-gray-100'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Zoom Out"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>

        <div className="flex items-center space-x-2 px-2">
          <input
            type="range"
            min="0.5"
            max="4"
            step="0.25"
            value={zoomLevel}
            onChange={(e) => onZoomChange(parseFloat(e.target.value))}
            className="w-24 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
          />
          <span className="text-xs text-gray-500 w-12 text-center">
            {Math.round(zoomLevel * 100)}%
          </span>
        </div>

        <button
          onClick={() => onZoomChange(zoomLevel + 0.25)}
          disabled={zoomLevel >= 4}
          className={`p-1.5 rounded transition-colors ${
            zoomLevel < 4
              ? 'text-gray-600 hover:bg-gray-100'
              : 'text-gray-300 cursor-not-allowed'
          }`}
          title="Zoom In"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Right: Custom actions */}
      <div className="flex items-center space-x-3">{rightContent}</div>
    </div>
  );
};

export default Toolbar;

